import { Link } from "react-router-dom";
import {
    Calendar,
    ClipboardCheck,
    Handshake,
    Key,
    Home,
    Search,
    ChevronRight,
} from "lucide-react";

// Per-stage contextual hint shown above the Reports list when the user
// has answered Phase 2's `property_stage` question. Pure UI nudge —
// never blocking, dismissible by changing the stage in Settings.
const STAGE_HINTS = {
    researching: {
        icon: Search,
        eyebrow: "While you research",
        headline: "Compare what to look for before you sign anything.",
        copy: "Use the Repair Exposure Calculator on a sample listing to see what a typical inspection range looks like.",
        cta: { label: "Try the calculator", to: "/tools/repair-exposure-calculator" },
    },
    scheduling_inspection: {
        icon: Calendar,
        eyebrow: "Inspection coming up",
        headline: "Get ready for inspection day.",
        copy: "Review what good inspectors flag and the right questions to ask — short read.",
        cta: { label: "Read the prep guide", to: "/learn" },
    },
    inspection_completed: {
        icon: ClipboardCheck,
        eyebrow: "Inspection done",
        headline: "Upload your report and we'll surface the leverage.",
        copy: "We organize findings into negotiation-ready summaries — most reports take ~3 minutes.",
        cta: { label: "Upload inspection", to: "/upload" },
    },
    under_contract: {
        icon: Key,
        eyebrow: "Under contract",
        headline: "Time-sensitive: build your negotiation summary.",
        copy: "Buyers under contract typically have 7–10 days to respond. Don't leave money on the table.",
        cta: { label: "Build negotiation summary", to: "/tools" },
    },
    negotiating_repairs: {
        icon: Handshake,
        eyebrow: "Negotiating repairs",
        headline: "Sharpen your ask with the leverage builder.",
        copy: "Pull the highest-leverage items + planning-range estimates into a clean send-to-agent summary.",
        cta: { label: "Open the builder", to: "/tools" },
    },
    closing_soon: {
        icon: Home,
        eyebrow: "Closing soon",
        headline: "Final walkthrough checklist.",
        copy: "Last chance to confirm agreed-upon repairs were actually completed. Free checklist.",
        cta: { label: "Open the checklist", to: "/learn" },
    },
    already_own: {
        icon: Home,
        eyebrow: "After closing",
        headline: "Keep tracking long-term repairs.",
        copy: "Save your reports for resale and refer back to planning ranges as you maintain the property.",
        cta: { label: "Browse repair guides", to: "/learn" },
    },
};

/**
 * Renders a small, dismissible-by-update contextual banner that
 * matches the user's current Phase 2 `property_stage`. Returns null
 * if the user hasn't answered the question, so it disappears silently
 * for users who haven't filled out Phase 2.
 */
export default function PropertyStageHint({ user }) {
    const stage = user?.property_stage;
    if (!stage) return null;
    const hint = STAGE_HINTS[stage];
    if (!hint) return null;

    const Icon = hint.icon;
    return (
        <div
            className="bl-card p-4 sm:p-5 bg-bl-teal-tint/30 border-bl-primary/20 flex items-start gap-3 mb-4"
            data-testid={`property-stage-hint-${stage}`}
        >
            <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-bl-primary" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                    {hint.eyebrow}
                </div>
                <div className="font-display font-semibold text-bl text-sm sm:text-base mt-0.5">
                    {hint.headline}
                </div>
                <p className="mt-1 text-xs sm:text-[13px] text-bl-muted leading-relaxed">
                    {hint.copy}
                </p>
                <Link
                    to={hint.cta.to}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-bl-primary hover:underline font-medium"
                    data-testid={`property-stage-hint-cta-${stage}`}
                >
                    {hint.cta.label}
                    <ChevronRight className="h-3 w-3" />
                </Link>
            </div>
        </div>
    );
}
